import { getBundleOverride } from './shared-components/bundle-override/build';

/**
 * List out all the environment variables we use here one by one,
 * so Webpack EnvironmentPlugin can detect + replace them at build time
 * We can't use something like const { BITBUCKET_COMMIT } = process.env
 * @see https://bitbucket.org/atlassian/dev-console/pull-requests/249/
 * This is the only place in the frontend codebase we are be allowed to use 'process.env',
 * hence the ESLint disable rule below
 */
/* eslint-disable no-restricted-globals */
export const env = {
  BITBUCKET_COMMIT: process.env.BITBUCKET_COMMIT,
  BITBUCKET_BUILD_NUMBER: process.env.BITBUCKET_BUILD_NUMBER,
  DEBUG_BROWSER_METRICS: process.env.DEBUG_BROWSER_METRICS,
  LD_CLIENT_KEY: process.env.LD_CLIENT_KEY,
  GRAPHQL_GATEWAY_URL_OVERRIDE: process.env.GRAPHQL_GATEWAY_URL_OVERRIDE,
  JEST_WORKER_ID: process.env.JEST_WORKER_ID,
  REACT_APP_SEGMENT_API_KEY_DEV: process.env.REACT_APP_SEGMENT_API_KEY_DEV,
  REACT_APP_SEGMENT_API_KEY_PROD: process.env.REACT_APP_SEGMENT_API_KEY_PROD,
  ROUTER_BASENAME: process.env.ROUTER_BASENAME,
  SEGMENT_API_KEY_DEV: process.env.SEGMENT_API_KEY_DEV,
  SEGMENT_API_KEY_PROD: process.env.SEGMENT_API_KEY_PROD,
  GOOGLE_TAG_MANAGER_CONTAINER_ID: process.env.GOOGLE_TAG_MANAGER_CONTAINER_ID
};
/* eslint-enable no-restricted-globals */

// Hostnames must stay in sync with https://extranet.atlassian.com/display/ECO/DAC+-+Feature+Flags
export const DAC_PROD_DOMAIN = 'https://developer.atlassian.com';
export const productionHostnames = [
  'dac.prod.atl-paas.net',
  'dac.us-east-1.prod.atl-paas.net',
  'developer.atlassian.com',
  'dev-console.us-west-2.prod.public.atl-paas.net',
];
export const fedRampProductionHostnames = [
  'developer.atlassian-us-gov-mod.com',
  'dac.us-east-1.prod.atl-paas-us-gov-mod.net',
];

export const fedRampStagingHostnames = [
  'developer.stg.atlassian-us-gov-mod.com',
  'dac.us-east-1.staging.atl-paas-us-gov-mod.net',
];

export const stagingHostnames = [
  'dac.staging.atl-paas.net',
  'dac.us-east-1.staging.atl-paas.net',
  'developer.stg.internal.atlassian.com',
  'dev-console.us-west-2.staging.public.atl-paas.net',
];

export const devHostnames = [
  'dac.dev.atl-paas.net',
  'dac--app.ap-southeast-2.dev.atl-paas.net',
  'dev-console.us-west-2.dev.public.atl-paas.net',
];

function isHTMLScriptElement(element: HTMLOrSVGScriptElement): element is HTMLScriptElement {
  return 'src' in element;
}

function getHostname(resolvedHostURL: string, fallbackHostname: string): string {
  try {
    const resolvedHostUrlObj = new URL(resolvedHostURL);
    return resolvedHostUrlObj.hostname;
  } catch {
    return fallbackHostname;
  }
}
export enum DevConsoleEnvironments {
  DEV = 'dev',
  PROD = 'prod',
  STAGING = 'staging',
  FedPROD = 'fedm-prod',
  FedSTAGING = 'fedm-staging',
  LOCAL = 'local',
}

/**
 * Returns the environment depending on the hostname.
 *
 * @param {string} [hostname=window.location.hostname] the hostname
 * @returns {string} one of 'prod', 'staging', 'dev', 'fedm-prod', 'fedm-staging', or 'local'.
 */

export function environment(hostname: string = window.location.hostname): DevConsoleEnvironments {
  const resolvedHostURL =
    document.currentScript && isHTMLScriptElement(document.currentScript) ? document.currentScript.src : hostname;

  const resolvedHostname = getHostname(resolvedHostURL, hostname);

  if (getBundleOverride(location.search)) {
    return DevConsoleEnvironments.DEV;
  }

  if (productionHostnames.includes(resolvedHostname)) {
    return DevConsoleEnvironments.PROD;
  }

  if (stagingHostnames.includes(resolvedHostname)) {
    return DevConsoleEnvironments.STAGING;
  }

  if (devHostnames.includes(resolvedHostname)) {
    return DevConsoleEnvironments.DEV;
  }

  if (fedRampProductionHostnames.includes(resolvedHostname)) {
    return DevConsoleEnvironments.FedPROD;
  }

  if (fedRampStagingHostnames.includes(resolvedHostname)) {
    return DevConsoleEnvironments.FedSTAGING;
  }

  return DevConsoleEnvironments.LOCAL;
}
export const isFederalEnv = (): boolean => {
  const currEnv = environment();
  if (currEnv === DevConsoleEnvironments.DEV && fedRampStagingHostnames.includes(window.location.hostname)) {
    return true;
  }
  return [DevConsoleEnvironments.FedSTAGING, DevConsoleEnvironments.FedPROD].includes(currEnv);
};

export function getMarketplaceBaseUrl(): string {
  return `https://${
    environment() === 'prod' ? 'marketplace-cdn.atlassian.com' : 'marketplace.stg.internal.atlassian.com'
  }`;
}

export function bitbucketVersion(): string {
  return env.BITBUCKET_COMMIT && env.BITBUCKET_BUILD_NUMBER
    ? `${env.BITBUCKET_COMMIT}:${env.BITBUCKET_BUILD_NUMBER}`
    : 'local-dev';
}

type EnvWindow = Window & {
  process: {
    env?: Record<string, unknown>;
  };
};

declare const window: EnvWindow;

/**
 * All dev-console modules should import from env, but for any third party dependencies that still rely on process.env,
 * we can polyfill it for them
 */
export function polyfillGlobalProcessEnv() {
  if (!window.process) {
    window.process = {};
  }
  if (!window.process.env) {
    window.process.env = {};
  }
  window.process.env = {
    ...window.process.env,
    ...env,
  };
}
polyfillGlobalProcessEnv();

export const formatDocumentationUrlForProducts = (docUrl?: string | null) => {
  /*
    From the getServices, some documentation links come with the origin, some come without (like just /cloud/product)
    Here we are checking if they have origin, the new URL operation will be successful and we will return the path with DAC prod origin
    Else we are returning the url as it is coming from graphql with DAC prod domain attached 
  */
  if (!docUrl) {
    return '';
  }
  try {
    const url = new URL(docUrl);
    const path = url.pathname;
    return DAC_PROD_DOMAIN + path;
  } catch {
    return DAC_PROD_DOMAIN + docUrl;
  }
};
